import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { useCallback } from 'react';
import {
    setInvalidationField,
    // setIsSubmitting,
    setInvalidationFormErrors,
    clearInvalidationFormErrors,
    resetInvalidationForm,
    setInvalidationForm,
    setFoundSaleToInvalidate,
    clearFoundSaleToInvalidate,
    setFoundReplacementSale,
    clearFoundReplacementSale,
    // clearReplacementBranch,
} from '../../store/slices/sale/saleInvalidationSlice';

import useConfirmModal from '../useConfirmModal';
import useModals from '../useModals';
import SaleInvalidationService from '../../services/sale/SaleInvalidationService';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';
import { SaleInvalidationDTO } from '../../types/sale/invalidation/SaleInvalidationDTO';
import { SelectedSaleInfo } from '../../types/sale/invalidation/SelectedSaleInfo';
import { SelectedSaleDetailDTO } from '../../types/sale/invalidation/SelectedSaleDetailDTO';
import { useNavigate } from 'react-router-dom';
import { mapPrefillToForm, mapSelectedSaleDetailToInfo, toCreatePayload } from '../../mappers/sale/saleInvalidationMapper';
import { useSale } from './useSale';

export const useSaleInvalidation = () => {
    const dispatch: AppDispatch = useDispatch();

    const {
        form,
        isSubmitting,
        formErrors,
        foundSaleToInvalidate,
        foundReplacementSale,
    } = useSelector((state: RootState) => state.saleInvalidation);

    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();
    const navigate = useNavigate();
    const confirm = useConfirmModal();
    const { openModal } = useModals();
    const { clearFilteredSales } = useSale();


    /**
     * 🔄 Actualiza un campo individual del formulario.
     */
    const updateField = useCallback(
        <K extends keyof SaleInvalidationDTO>(field: K, value: SaleInvalidationDTO[K]) => {
            dispatch(setInvalidationField({ field, value }));
        },
        [dispatch]
    );

    /**
     * 🧩 Mapper: SelectedSaleDetailDTO (backend) -> SelectedSaleInfo (estado UI)
     * Ajusta este mapper si tu SelectedSaleInfo difiere.
     */
    const mapToSelectedSaleInfo = (dto: SelectedSaleDetailDTO): SelectedSaleInfo => ({
        // saleId: no viene del backend. Si tu SelectedSaleInfo lo exige, marca como undefined u opcional.
        generationCode: dto.generationCode,
        controlNumber: dto.controlNumber,
        saleDate: dto.saleDate,
        documentTypeName: dto.documentTypeName,
        customerName: dto.customerName,
        total: dto.total,
    });

    /**
   * 📤 Envía el evento de anulación al backend con confirmación previa.
   * - Valida campos obligatorios.
   * - Pide confirmación al usuario.
   * - Envía payload con los nombres EXACTOS que espera el backend.
   * - Limpia/Resetea estado y muestra modal de resultado.
   */
    const submitInvalidation = () => {
        confirm({
            title: 'Confirmar envío de invalidación',
            message:
                'Se notificará a Hacienda que el DTE quedará “INVALIDADO”.\n' +
                'Debe hacerse dentro del plazo legal y por motivos justificados.\n' +
                'Esta acción es irreversible.\n\n' +
                '¿Desea continuar?',
            confirmText: 'Registrar anulación',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                try {
                    // dispatch(setIsSubmitting(true));
                    showLoadingIndicator();

                    const payload = toCreatePayload(form);
                    console.log(payload);
                    const response = await SaleInvalidationService.register(payload);
                    const { type, title, message, statusCode } = response;

                    if (statusCode === 201) {
                        openModal(type, {
                            title,
                            message,
                        });

                        // Limpieza de formulario y resultados de búsqueda
                        dispatch(resetInvalidationForm());
                        clearFilteredSales();
                    }
                } catch (error) {
                    handle(error);

                    console.log(error)
                } finally {
                    // dispatch(setIsSubmittingInvalidation(false));
                    hideLoadingIndicator();
                }
            },
        });
    };


    /**
     * 🧯 Cargar prefill desde saleId (sin mutar el form automáticamente, para que el UI decida).
     * Si prefieres que setee campos del form, puedo dejarte la variante que hace updateField().
     */
    const loadPrefillFromSale = async (saleId: number) => {
        try {
            showLoadingIndicator();
            const response = await SaleInvalidationService.getPrefillFromSale(saleId);
            const { data, statusCode } = response;
            if (statusCode === 200) {
                const mapped = mapPrefillToForm(data)
                dispatch(setInvalidationForm(mapped));
                navigate('/sale-cancel')
                clearFilteredSales();
            }
        } catch (e) {
            handle(e);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 🔍 Busca venta a anular por código de generación y la asigna al estado.
     */
    const searchSaleToInvalidate = async (generationCode: string) => {
        try {
            showLoadingIndicator();
            const response = await SaleInvalidationService.findSaleToInvalidate(generationCode);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                const mapped = mapSelectedSaleDetailToInfo(data);
                dispatch(setFoundSaleToInvalidate(mapped));
            }
        } catch (e) {
            handle(e);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 🔍 Busca venta de reemplazo por código de generación y la asigna al estado.
     */
    const searchReplacementSale = async (generationCode: string) => {
        try {
            showLoadingIndicator();
            const response = await SaleInvalidationService.findReplacementSale(generationCode);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                const mapped = mapToSelectedSaleInfo(data);
                dispatch(setFoundReplacementSale(mapped));
            }
        } catch (e) {
            handle(e);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 🔁 Reinicia todo el formulario y selecciones.
     */
    const resetForm = () => {
        dispatch(resetInvalidationForm());
    };

    // en useSaleInvalidation
    const onChangeInvalidationType = useCallback((raw: string | number) => {
        const value = (raw ?? '').toString() as '1' | '2' | '3' | '';
        dispatch(setInvalidationField({ field: 'invalidationTypeCode', value: value || null }));
        if (value === '2') {
            dispatch(clearFoundReplacementSale());
            dispatch(setInvalidationField({ field: 'replacementGenerationCode', value: null }));
        }
    }, [dispatch]);


    return {
        // form
        form,
        isSubmitting,
        formErrors,
        updateField,
        submitInvalidation,
        resetForm,

        // prefill
        loadPrefillFromSale,

        // búsquedas
        searchSaleToInvalidate,
        searchReplacementSale,

        // resultados y banderas (para el UI de cards)
        foundSaleToInvalidate,
        foundReplacementSale,

        // helpers de errores
        setErrors: (errors: Record<string, string>) => dispatch(setInvalidationFormErrors(errors)),
        clearErrors: () => dispatch(clearInvalidationFormErrors()),

        // limpiar resultados de búsqueda puntualmente
        clearFoundSaleToInvalidate: () => dispatch(clearFoundSaleToInvalidate()),
        clearFoundReplacementSale: () => dispatch(clearFoundReplacementSale()),

        onChangeInvalidationType,

    };
};